import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookingService } from '@/services/booking.service';
import { FacilityType } from '@/types/booking.types';
import { shortCacheQueryOptions } from '@/config/react-query.config';

export const bookingEligibilityKeys = {
  all: ['booking-eligibility'] as const,
  forDate: (
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    rescheduleBookingId?: string
  ) =>
    [
      ...bookingEligibilityKeys.all,
      locationId,
      facility,
      bookingDate,
      rescheduleBookingId ?? 'NO_RESCHEDULE',
    ] as const,
};

/**
 * Whether the member may book `facility` on `bookingDate`.
 *
 * Most facilities don't need this: get-availability refuses with a reason
 * before returning slots, and the slot screen shows that reason on the first
 * screen. Hitting bay reads its slots from a 7-day endpoint that has no member
 * gate, so without this a member who cannot book (e.g. a pending membership)
 * walked all the way to the confirm sheet before being told. `error` carries
 * the server's reason — read it with `getApiMessage`, as for get-availability.
 *
 * Pass `enabled: false` (or an empty locationId/date) to skip the request.
 */
export const useBookingEligibility = (
  locationId: string,
  facility: FacilityType,
  bookingDate: string,
  rescheduleBookingId?: string,
  enabled = true
) => {
  const query = useQuery<{ allowed: true }, Error>({
    queryKey: bookingEligibilityKeys.forDate(
      locationId,
      facility,
      bookingDate,
      rescheduleBookingId
    ),
    queryFn: () =>
      BookingService.checkEligibility(
        locationId,
        facility,
        bookingDate,
        rescheduleBookingId
      ),
    enabled: enabled && Boolean(locationId && facility && bookingDate),
    ...shortCacheQueryOptions,
  });

  /* Each date is its own request. While the next date's answer is in
     flight, keep showing the last one rather than a spinner (or a flash of
     slots for a member who was just told they cannot book). Only the very
     first answer on the screen is waited for. */
  const settled = useRef<{ error: Error | null } | null>(null);
  if (!query.isPending) settled.current = { error: query.error ?? null };
  const waiting = query.isPending && query.fetchStatus === 'fetching';

  return {
    isLoading: waiting && settled.current === null,
    error: waiting && settled.current ? settled.current.error : query.error,
  };
};
